import { PublicClientApplication, InteractionRequiredAuthError } from '@azure/msal-browser'
import { Client } from '@microsoft/microsoft-graph-client'

const SCOPES = ['https://graph.microsoft.com/Calendars.Read']
const CONNECTION_PREFIX = 'spcc:outlook:'
let msalPromise
let sessionGeneration = 0

export const isOutlookConfigured = () => /^[\da-f]{8}-[\da-f]{4}-[\da-f]{4}-[\da-f]{4}-[\da-f]{12}$/i.test(
  (process.env.REACT_APP_MICROSOFT_CLIENT_ID || '').trim()
)

function getMsal() {
  if (!isOutlookConfigured()) {
    throw new Error('Outlook Calendar is not configured. Ask your SPCC administrator to complete the Microsoft app registration.')
  }
  if (!msalPromise) {
    const tenant = (process.env.REACT_APP_MICROSOFT_TENANT_ID || 'common').trim()
    if (!/^[a-z\d.-]+$/i.test(tenant)) throw new Error('The Microsoft tenant configuration is invalid.')
    const instance = new PublicClientApplication({
      auth: {
        clientId: process.env.REACT_APP_MICROSOFT_CLIENT_ID.trim(),
        authority: `https://login.microsoftonline.com/${tenant}`,
        // MSAL 4 polls this dedicated page; it must not redirect or close itself.
        redirectUri: `${window.location.origin}/microsoft-callback.html`,
      },
      cache: { cacheLocation: 'sessionStorage' },
    })
    msalPromise = instance.initialize().then(() => instance).catch(error => {
      msalPromise = undefined
      throw error
    })
  }
  return msalPromise
}

export function outlookErrorMessage(error) {
  const code = error?.errorCode || error?.code
  if (error instanceof InteractionRequiredAuthError || ['interaction_required', 'login_required', 'consent_required', 'invalid_grant'].includes(code)) {
    return 'Microsoft needs you to reconnect. Select Reconnect Outlook to continue.'
  }
  if (code === 'user_cancelled') return 'Microsoft sign-in was cancelled. You can try connecting again.'
  if (['popup_window_error', 'empty_window_error'].includes(code)) return 'Allow pop-ups for SPCC, then select Connect Outlook again.'
  if (code === 'interaction_in_progress') return 'Finish the Microsoft sign-in window already open, then try again.'
  if (['monitor_popup_timeout', 'monitor_window_timeout'].includes(code)) return 'Microsoft sign-in timed out. Try again and complete the sign-in window.'
  if (code === 'access_denied' || error?.statusCode === 403) return 'Calendar access was denied. Reconnect and allow calendar access; your organization may require administrator approval.'
  if (error?.statusCode === 401) return 'Your Outlook session expired. Select Reconnect Outlook to continue.'
  if (error?.statusCode === 429) return 'Microsoft is receiving too many requests. Wait a moment, then try again.'
  if (['MailboxNotEnabledForRESTAPI', 'ErrorMailboxNotEnabledForRESTAPI'].includes(code)) return 'This Microsoft account does not have an Outlook mailbox available for calendar access.'
  return error?.message || 'Could not connect to Outlook Calendar. Please try again.'
}

export async function clearOutlookSession() {
  sessionGeneration += 1
  // Only public account identifiers are stored here. MSAL manages tokens itself.
  Object.keys(sessionStorage).filter(key => key.startsWith(CONNECTION_PREFIX)).forEach(key => sessionStorage.removeItem(key))
  if (isOutlookConfigured()) await (await getMsal()).clearCache()
}

export class OutlookCalendarService {
  constructor(userId) {
    this.userId = userId
    this.msalInstance = null
    this.account = null
    this.isAuthenticated = false
    this.needsReconnect = false
    this.graphClient = null
  }

  get connectionKey() { return `${CONNECTION_PREFIX}${this.userId}` }

  async initialize() {
    if (!this.userId || !isOutlookConfigured()) return
    this.msalInstance = await getMsal()
    let saved
    try { saved = JSON.parse(sessionStorage.getItem(this.connectionKey)) } catch { sessionStorage.removeItem(this.connectionKey) }
    // Never choose the first cached Microsoft account: the connection belongs to an SPCC user.
    this.account = saved && this.msalInstance.getAllAccounts().find(account =>
      account.homeAccountId === saved.homeAccountId && account.localAccountId === saved.localAccountId && account.tenantId === saved.tenantId
    )
    this.isAuthenticated = !!this.account
    if (this.account) this.setupGraphClient()
  }

  async authenticate() {
    if (!this.userId) throw new Error('Sign in to SPCC before connecting your calendar.')
    const generation = sessionGeneration
    if (!this.msalInstance) this.msalInstance = await getMsal()
    const response = await this.msalInstance.loginPopup({ scopes: SCOPES, prompt: 'select_account' })
    if (!response.account) throw new Error('Microsoft did not return an account. Please try again.')
    if (generation !== sessionGeneration) {
      await this.msalInstance.clearCache({ account: response.account })
      throw new Error('Your SPCC session changed. Sign in to SPCC and connect Outlook again.')
    }
    const { homeAccountId, localAccountId, tenantId } = response.account
    sessionStorage.setItem(this.connectionKey, JSON.stringify({ homeAccountId, localAccountId, tenantId }))
    this.account = response.account
    this.isAuthenticated = true
    this.needsReconnect = false
    this.setupGraphClient()
    return response.account
  }

  setupGraphClient() {
    this.graphClient = Client.initWithMiddleware({ authProvider: { getAccessToken: async () => {
      if (!this.account || !sessionStorage.getItem(this.connectionKey)) throw new Error('Connect Outlook to import calendar events.')
      const generation = sessionGeneration
      try {
        const response = await this.msalInstance.acquireTokenSilent({ scopes: SCOPES, account: this.account })
        if (generation !== sessionGeneration || !sessionStorage.getItem(this.connectionKey)) {
          throw new Error('Connect Outlook to import calendar events.')
        }
        return response.accessToken
      } catch (error) {
        if (error instanceof InteractionRequiredAuthError || ['interaction_required', 'login_required', 'consent_required', 'invalid_grant'].includes(error.errorCode)) {
          this.needsReconnect = true
        }
        // Interactive recovery must come from the user's Reconnect button, not a background request.
        throw error
      }
    } } })
  }

  async getPages(request) {
    const items = []
    do {
      const page = await request.header('Prefer', 'outlook.timezone="UTC", IdType="ImmutableId"').get()
      items.push(...(page.value || []))
      const next = page['@odata.nextLink']
      if (next) {
        const url = new URL(next)
        if (url.origin !== 'https://graph.microsoft.com' || !url.pathname.startsWith('/v1.0/')) {
          throw new Error('Microsoft returned an invalid calendar page link.')
        }
      }
      request = next ? this.graphClient.api(next) : null
    } while (request)
    return items
  }

  requireConnection() {
    if (!this.isAuthenticated || !this.graphClient) throw new Error('Connect Outlook to import calendar events.')
  }

  async getEvents(startDate, endDate, calendarId = '') {
    this.requireConnection()
    if (!Number.isFinite(startDate?.getTime()) || !Number.isFinite(endDate?.getTime()) || endDate <= startDate) {
      throw new Error('Choose a valid start and end date.')
    }
    try {
      const path = calendarId ? `/me/calendars/${encodeURIComponent(calendarId)}/calendarView` : '/me/calendarView'
      const events = await this.getPages(this.graphClient.api(path)
        .query({ startDateTime: startDate.toISOString(), endDateTime: endDate.toISOString() })
        .select('id,subject,start,end,bodyPreview,body,onlineMeeting,onlineMeetingUrl,location,isAllDay,isCancelled,responseStatus')
        .orderby('start/dateTime').top(100))
      // All-day items are reminders/availability, not measured working time.
      return events.filter(event => !event.isCancelled && !event.isAllDay && event.responseStatus?.response !== 'declined')
    } catch (error) {
      if ([401, 403].includes(error.statusCode)) this.needsReconnect = true
      throw new Error(outlookErrorMessage(error))
    }
  }

  async getCalendars() {
    this.requireConnection()
    try {
      return await this.getPages(this.graphClient.api('/me/calendars').select('id,name,isDefaultCalendar').top(100))
    } catch (error) {
      if ([401, 403].includes(error.statusCode)) this.needsReconnect = true
      throw new Error(outlookErrorMessage(error))
    }
  }

  async disconnect() {
    const account = this.account
    sessionStorage.removeItem(this.connectionKey)
    this.account = null
    this.isAuthenticated = false
    this.needsReconnect = false
    this.graphClient = null
    // Disconnect locally without logging the user out of Microsoft in other applications.
    if (account && this.msalInstance) await this.msalInstance.clearCache({ account })
  }
}
